import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
// getApiErrorMessage / getApiErrorCode kaldırıldı — handleEnroll silindi
import { Star, Users, BookOpen, Zap, Play, CheckCircle, ChevronRight, Lock, Heart, MessageSquare, Send, FileCheck, MessageCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { useReducedMotion } from 'framer-motion';
import { fadeUp, staggerContainer, buttonSquash, bounceIn, maybe, VIEWPORT_ONCE } from '../lib/motionVariants';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import axios from 'axios';
import { getDisplayTitle } from '../lib/landingCourses';
import WhatsAppContactButton from '../components/WhatsAppContactButton';
import CourseCover from '../components/course/CourseCover';
import { isAssessmentUnlocked } from '../utils/assessment';
import { getStudentCount } from '../lib/studentCount';
import { hasRating } from '../lib/courseRating';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Ders tipi etiketi ────────────────────────────────────────────────────────
// lesson_type değerleri LessonDispatcher.jsx REGISTRY ile uyumlu olmalı.
const LESSON_TYPE_LABEL_KEYS = {
  video: 'courseDetail.lessonType.video',
  interactive: 'courseDetail.lessonType.interactive',
  pdf: 'courseDetail.lessonType.pdf',
  text: 'courseDetail.lessonType.text',
  quiz: 'courseDetail.lessonType.quiz',
  exam: 'courseDetail.lessonType.exam',
  flashcard: 'courseDetail.lessonType.flashcard',
};
function getLessonTypeLabel(lessonType, t) {
  const key = LESSON_TYPE_LABEL_KEYS[lessonType] || 'courseDetail.lessonType.default';
  return t(key);
}

// ─── Lesson satırı ───────────────────────────────────────────────────────────
function LessonRow({ lesson, index, enrollment, t, getField, reduced, handleStartLesson }) {
  const isEnrolled = !!enrollment;
  const isCompleted = enrollment?.completed_lessons?.includes(lesson.lesson_id);
  const isAccessible = isEnrolled;
  return (
    <motion.div key={lesson.lesson_id} variants={maybe(reduced, fadeUp)}>
      <button
        onClick={isAccessible ? () => handleStartLesson(lesson.lesson_id) : undefined}
        aria-disabled={!isAccessible}
        disabled={!isAccessible}
        title={!isEnrolled ? t('courseDetail.lesson.locked') : undefined}
        className={`w-full card-duo p-4 flex items-center gap-4 text-left transition-all ${isAccessible ? 'hover:border-[var(--duo-blue)] cursor-pointer' : 'opacity-60 cursor-not-allowed'}`}
        data-testid={`lesson-item-${index}`}
      >
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isCompleted ? 'bg-[var(--duo-green)]' : !isAccessible ? 'bg-[var(--duo-stroke)]' : lesson.lesson_type === 'interactive' ? 'bg-[var(--duo-orange)]' : 'bg-[var(--duo-stroke)]'}`}>
          {isCompleted ? <CheckCircle size={20} strokeWidth={2.5} className="text-white" /> :
           !isAccessible ? <Lock size={20} strokeWidth={2.5} className="text-[var(--duo-text-soft)]" /> :
           lesson.lesson_type === 'interactive' ? <Zap size={20} strokeWidth={2.5} className="text-white" /> :
           <Play size={20} strokeWidth={2.5} className="text-[var(--duo-text-soft)]" />}
        </div>
        <div className="flex-1">
          <h4 className="font-bold text-sm">{getField(lesson, 'title')}</h4>
          <p className="text-xs text-[var(--duo-text-soft)] font-medium mt-1">
            {getLessonTypeLabel(lesson.lesson_type, t)}
            {lesson.duration_minutes > 0 && ` • ${lesson.duration_minutes} min`}
          </p>
        </div>
        {!isAccessible && (
          <span className="text-xs font-semibold text-[var(--duo-text-soft)]">
            {t('courseDetail.lesson.locked')}
          </span>
        )}
        {isAccessible && (
          <ChevronRight size={18} strokeWidth={2.5} className="text-[var(--duo-text-soft)]" />
        )}
      </button>
    </motion.div>
  );
}

/**
 * Dersleri modüle göre gruplar.
 * DB'de module_id + module_num + module_title_tr/module_title_en alanları SET ise
 * modül bazlı accordion gösterir. Modül bilgisi yoksa düz liste olarak düşer.
 */
function groupLessonsByModule(lessons, _lang) {
  // module_id olan en az bir ders varsa gruplu göster
  const hasModules = lessons.some((l) => l.module_id || l.module_num);
  if (!hasModules) return null; // null → düz liste

  const groups = [];
  const seen = new Map(); // module_id → group index

  for (const lesson of lessons) {
    const key = lesson.module_id || `no_module_${lesson.order}`;
    if (!seen.has(key)) {
      const titleTr = lesson.module_title_tr || lesson.module_title || `Modül ${lesson.module_num || ''}`.trim();
      const titleEn = lesson.module_title_en || lesson.module_title || `Module ${lesson.module_num || ''}`.trim();
      groups.push({
        key,
        // module_num artık backend'de SAYISAL değil, "M1"/"M5" gibi görüntüleme
        // etiketi (STRING). Sıralamaya karıştırılmaz — sadece başlıkta kullanılır.
        // Sıralama tekil kaynağı: module_order (backend _lesson_sort_pipeline ile
        // zaten module_order → order sırasına göre sıralanmış olarak gelir).
        moduleOrder: typeof lesson.module_order === 'number' ? lesson.module_order : null,
        titleTr,
        titleEn,
        lessons: [],
      });
      seen.set(key, groups.length - 1);
    }
    groups[seen.get(key)].lessons.push(lesson);
  }

  // Birincil sıralama anahtarı: module_order (sayısal, 0 dahil geçerli — ?? nullish
  // olduğu için 0'ı 999999 sentinel'e düşürmez). module_order eksik/null olan
  // gruplar backend sentinel'iyle (999999) uyumlu şekilde sona düşer. Eşitlik
  // durumunda (ör. hepsi null) Array.sort ES2019+ stabil olduğundan grupların
  // backend'den gelen orijinal sırası (module_order → order pipeline'ı) korunur.
  groups.sort((a, b) => (a.moduleOrder ?? 999999) - (b.moduleOrder ?? 999999));
  return groups;
}

// ─── Değerlendirme / Assessment kartı — müfredat listesinin sonunda HER ZAMAN görünür.
// Kursun course.final_exam.enabled=true ise gösterilir (backend GET /courses/{id} —
// _strip_final_exam_answers ile enabled/passing_score/time_limit_minutes/max_attempts
// meta alanları döner, sorular gizlenir). Tamamlanmadan önce kilitli ama görünür kalır. ──
function AssessmentCard({ course, enrollment, t, navigate, courseId, lessons }) {
  const examEnabled = !!course?.final_exam?.enabled;
  if (!examEnabled) return null;

  // Kilit durumu paylaşılan formülle hesaplanır (bkz. utils/assessment.js) —
  // CabinLessonLayout'taki sidebar kilidiyle aynı kaynağı kullanır, ıraksama olmaz.
  const unlocked = !!enrollment && isAssessmentUnlocked({
    enrollment,
    lessons,
    completedLessonIds: enrollment?.completed_lessons || [],
  });
  const title = t('courseDetail.curriculum.assessment.title');
  const sub = unlocked
    ? t('courseDetail.curriculum.assessment.unlockedSub')
    : t('courseDetail.curriculum.assessment.lockedSub');

  return (
    <div
      className={`mt-4 rounded-2xl border-2 p-4 flex items-center gap-4 ${
        unlocked
          ? 'bg-[var(--surface-warm)] border-[var(--brand-accent-light)]'
          : 'bg-gray-50 border-[var(--duo-stroke)] opacity-90'
      }`}
      data-testid="curriculum-assessment-card"
    >
      <div
        className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
          unlocked ? 'bg-[var(--duo-orange)]' : 'bg-[var(--duo-stroke)]'
        }`}
        aria-hidden="true"
      >
        {unlocked
          ? <FileCheck size={20} strokeWidth={2.5} className="text-white" />
          : <Lock size={18} strokeWidth={2.5} className="text-[var(--duo-text-soft)]" />}
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-bold text-sm">{title}</h4>
        <p className="text-xs text-[var(--duo-text-soft)] font-medium mt-0.5">{sub}</p>
      </div>
      {unlocked ? (
        <button
          onClick={() => navigate(`/exam/${courseId}`)}
          className="btn-duo-blue px-4 py-2 text-xs flex-shrink-0"
          data-testid="curriculum-assessment-cta"
        >
          {t('courseDetail.curriculum.assessment.cta')}
        </button>
      ) : (
        <span
          className="flex items-center gap-1 text-xs font-semibold text-[var(--duo-text-soft)] flex-shrink-0"
          aria-disabled="true"
          aria-label={`${title} — ${sub}`}
          data-testid="curriculum-assessment-locked-badge"
        >
          <Lock size={14} strokeWidth={2.5} />
          {t('courseDetail.curriculum.assessment.lockedBadge')}
        </span>
      )}
    </div>
  );
}

// ─── Müfredat sekmesi ─────────────────────────────────────────────────────────
function CurriculumTab({ lessons, enrollment, course, lang, t, getField, reduced, handleStartLesson, navigate, courseId }) {
  const [expandedGroups, setExpandedGroups] = useState({});
  const groups = groupLessonsByModule(lessons, lang);

  const toggleGroup = (key) =>
    setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] }));

  let body;

  if (lessons.length === 0) {
    body = (
      <div className="text-center py-12 text-[var(--duo-text-soft)]">
        <BookOpen size={40} className="mx-auto mb-3" />
        <p className="font-semibold">{t('courseDetail.curriculum.empty')}</p>
      </div>
    );
  } else if (!groups) {
    // Modül bilgisi yoksa eski düz liste davranışını koru (geriye uyumluluk)
    body = (
      <motion.div
        className="space-y-3"
        variants={maybe(reduced, staggerContainer())}
        initial="hidden"
        animate="visible"
      >
        {lessons.map((lesson, i) => (
          <LessonRow
            key={lesson.lesson_id}
            lesson={lesson}
            index={i}
            enrollment={enrollment}
            t={t}
            getField={getField}
            reduced={reduced}
            handleStartLesson={handleStartLesson}
          />
        ))}
      </motion.div>
    );
  } else {
    // Modül bazlı accordion gösterim
    body = (
      <div className="space-y-4" data-testid="curriculum-modules">
        {groups.map((group, gi) => {
          const isOpen = expandedGroups[group.key] !== false; // varsayılan açık
          const groupTitle = lang === 'tr' ? group.titleTr : group.titleEn;
          const completedInGroup = group.lessons.filter(
            (l) => enrollment?.completed_lessons?.includes(l.lesson_id)
          ).length;
          return (
            <div
              key={group.key}
              className="rounded-2xl border-2 border-[var(--duo-stroke)] bg-white overflow-hidden shadow-[0_2px_0_0_var(--duo-stroke)]"
              data-testid={`module-group-${gi}`}
            >
              <button
                className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-gray-50 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--duo-blue)]"
                onClick={() => toggleGroup(group.key)}
                aria-expanded={isOpen}
              >
                <BookOpen size={18} strokeWidth={2.5} className="text-[var(--duo-blue)] shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm text-[var(--duo-text)] truncate">{groupTitle}</p>
                  <p className="text-xs text-[var(--duo-text-soft)]">
                    {completedInGroup}/{group.lessons.length} {lang === 'tr' ? 'ders tamamlandı' : 'lessons done'}
                  </p>
                </div>
                {/* Grup ilerleme barı */}
                {group.lessons.length > 0 && (
                  <div className="shrink-0 w-20 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[var(--duo-green)] rounded-full transition-all"
                      style={{ width: `${(completedInGroup / group.lessons.length) * 100}%` }}
                    />
                  </div>
                )}
                {isOpen
                  ? <ChevronRight size={16} className="text-[var(--duo-text-soft)] shrink-0 rotate-90 transition-transform" />
                  : <ChevronRight size={16} className="text-[var(--duo-text-soft)] shrink-0 transition-transform" />}
              </button>
              {isOpen && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="border-t border-[var(--duo-stroke)] px-2 pb-2 flex flex-col gap-1"
                >
                  {group.lessons.map((lesson, li) => (
                    <LessonRow
                      key={lesson.lesson_id}
                      lesson={lesson}
                      index={`${gi}-${li}`}
                      enrollment={enrollment}
                      t={t}
                      getField={getField}
                      reduced={reduced}
                      handleStartLesson={handleStartLesson}
                    />
                  ))}
                </motion.div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div>
      {body}
      {/* Modül 3.7.3 / CPD blokaj çözümü: final sınavı müfredatın SONUNDA her zaman
          görünür bir kart olarak yer alır — %100 tamamlanana kadar kilitli ama var olduğu belli. */}
      <AssessmentCard
        course={course}
        enrollment={enrollment}
        t={t}
        navigate={navigate}
        courseId={courseId}
        lessons={lessons}
      />
    </div>
  );
}

function ReviewForm({ courseId, authHeaders, onSubmit, t, lang: _lang }) {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0 || !comment.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, { course_id: courseId, rating, comment }, { headers: authHeaders() });
      setSubmitted(true);
      setComment('');
      setRating(0);
      if (onSubmit) await onSubmit();
    } catch (err) { console.error(err); }
    finally { setSubmitting(false); }
  };

  if (submitted) return (
    <div className="card-duo p-5 text-center bg-green-50 border-[var(--brand-success)]" data-testid="review-submitted">
      <CheckCircle size={24} className="mx-auto text-[var(--brand-success)] mb-2" />
      <p className="font-bold text-sm text-[var(--brand-success)]">
        {t('courseDetail.reviewForm.submitted')}
      </p>
    </div>
  );

  return (
    <div className="card-duo p-5 mb-4" data-testid="review-form">
      <div className="flex items-center gap-2 mb-3">
        <MessageSquare size={18} strokeWidth={2.5} className="text-[var(--duo-blue)]" />
        <h4 className="font-bold text-sm">{t('courseDetail.reviewForm.title')}</h4>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="flex gap-1 mb-3">
          {[1, 2, 3, 4, 5].map(s => (
            <button key={s} type="button" onClick={() => setRating(s)}
              onMouseEnter={() => setHoverRating(s)} onMouseLeave={() => setHoverRating(0)}
              className="p-1 transition-transform hover:scale-110" data-testid={`star-${s}`}>
              <Star size={24} strokeWidth={2.5}
                className={(hoverRating || rating) >= s ? 'text-[var(--duo-orange)]' : 'text-gray-200'}
                fill={(hoverRating || rating) >= s ? 'var(--duo-orange)' : 'none'} />
            </button>
          ))}
          {rating > 0 && <span className="ml-2 text-sm font-bold text-[var(--duo-text-soft)]">{rating}/5</span>}
        </div>
        <div className="flex gap-2">
          <textarea value={comment} onChange={e => setComment(e.target.value)}
            placeholder={t('courseDetail.reviewForm.placeholder')}
            rows={2} className="flex-1 px-4 py-2.5 rounded-xl border-2 border-[var(--duo-stroke)] bg-gray-50 focus:border-[var(--duo-blue)] focus:outline-none font-semibold text-sm resize-none"
            data-testid="review-comment-input" />
          <button type="submit" disabled={submitting || rating === 0 || !comment.trim()}
            className="btn-duo-blue px-4 py-2 self-end disabled:opacity-50" data-testid="submit-review-btn">
            <Send size={18} />
          </button>
        </div>
      </form>
    </div>
  );
}

export default function CourseDetail() {
  const { courseId } = useParams();
  const { t, getField, lang } = useLanguage();
  const { user, token, authHeaders } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [enrollment, setEnrollment] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [loading, setLoading] = useState(true);

  const reduced = useReducedMotion();

  useEffect(() => {
    document.body.classList.add('duo-page');
    return () => document.body.classList.remove('duo-page');
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/courses/${courseId}`);
        setCourse(res.data.course);
        setLessons(res.data.lessons);
        setReviews(res.data.reviews);

        if (token) {
          try {
            const [enrollRes, favRes] = await Promise.all([
              axios.get(`${API}/enrollments`, { headers: authHeaders() }),
              axios.get(`${API}/favorites`, { headers: authHeaders() })
            ]);
            const found = enrollRes.data.enrollments?.find(e => e.course_id === courseId);
            if (found) setEnrollment(found);
            setIsFavorite(favRes.data.favorites?.some(f => f.course_id === courseId) || false);
          } catch { /* kayıt/favori yüklenemedi — opsiyonel veri, sessizce devam */ }
        }
      } catch (err) {
        console.error('Error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, token]);

  // handleEnroll kaldırıldı — self-enrollment devre dışı (Modül self-enroll disable)

  const toggleFavorite = async () => {
    if (!user) { navigate('/login'); return; }
    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/${courseId}`, { headers: authHeaders() });
      } else {
        await axios.post(`${API}/favorites`, { course_id: courseId }, { headers: authHeaders() });
      }
      setIsFavorite(!isFavorite);
    } catch (err) { console.error(err); }
  };

  const handleStartLesson = (lessonId) => {
    navigate(`/lesson/${courseId}/${lessonId}`);
  };

  // Faz 2: Kaldigi yerden devam (last_lesson_id varsa ona git, yoksa ilk derse)
  const handleContinue = () => {
    const targetLessonId = enrollment?.last_lesson_id
      && lessons.some(l => l.lesson_id === enrollment.last_lesson_id)
        ? enrollment.last_lesson_id
        : (lessons[0]?.lesson_id);
    if (!targetLessonId) {
      toast.warning(t('courseDetail.curriculum.empty'));
      return;
    }
    navigate(`/lesson/${courseId}/${targetLessonId}`);
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg-app)]">
      <div
        className="w-10 h-10 border-4 border-[var(--duo-blue)] border-t-transparent rounded-full animate-spin"
        role="status"
        aria-label={t('loading')}
      />
    </div>
  );

  if (!course) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[var(--bg-app)] gap-4">
      <BookOpen size={48} strokeWidth={2} className="text-[var(--duo-text-soft)]" />
      <p className="font-duo-display text-xl">{t('courseDetail.notFound')}</p>
      <Link to="/courses" className="btn-duo-secondary px-6 py-3">
        {t('browse_courses')}
      </Link>
    </div>
  );

  // DÜZELTME (reviewer bulgusu #3, landing-v2-duzeltmeler): landing'de
  // getDisplayTitle ile override edilen kurs adları (örn. "İş İngilizcesi" →
  // "Havacılık İngilizcesi") burada uygulanmıyordu — kullanıcı landing'deki
  // karta tıklayıp detay sayfasında FARKLI (ham) bir başlık görüyordu. Tek
  // kaynak artık burada da kullanılıyor (bkz. lib/landingCourses.js).
  const courseDisplayTitle = getDisplayTitle(course, lang, getField);

  return (
    <div className="min-h-screen bg-[var(--bg-app)]" data-testid="course-detail-page">
      {/* Hero */}
      <motion.section
        className="bg-white border-b-2 border-[var(--duo-stroke)]"
        variants={maybe(reduced, fadeUp)}
        initial="hidden"
        whileInView="visible"
        viewport={VIEWPORT_ONCE}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="md:col-span-2">
              <div className="flex flex-wrap gap-2 mb-4">
                {course.is_interactive && (
                  <span className="bg-[var(--duo-orange)] text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                    <Zap size={12} strokeWidth={2.5} /> {t('interactive_course')}
                  </span>
                )}
                <span className="bg-[var(--duo-stroke)] text-[var(--duo-text-soft)] text-xs font-bold px-3 py-1 rounded-full">
                  {t(course.level)}
                </span>
              </div>
              <h1 className="font-duo-display text-3xl md:text-4xl font-bold mb-4" data-testid="course-title">
                {courseDisplayTitle}
              </h1>
              <p className="text-[var(--duo-text-soft)] font-medium mb-6 text-base leading-relaxed flex items-start gap-3">
                <span className="flex-1">{getField(course, 'description')}</span>
                <button
                  onClick={toggleFavorite}
                  aria-label={isFavorite ? t('courseDetail.fav.remove') : t('courseDetail.fav.add')}
                  className={`flex-shrink-0 w-10 h-10 rounded-xl border-2 flex items-center justify-center transition-all ${isFavorite ? 'bg-red-50 border-red-200' : 'bg-white border-[var(--duo-stroke)] hover:border-red-200'}`}
                  data-testid="favorite-btn"
                >
                  <Heart size={20} strokeWidth={2.5} className={isFavorite ? 'text-[var(--brand-danger)]' : 'text-[var(--duo-text-soft)]'} fill={isFavorite ? 'var(--brand-danger)' : 'none'} />
                </button>
              </p>
              <div className="flex flex-wrap items-center gap-4 text-sm">
                {hasRating(course) && (
                  <div className="flex items-center gap-1">
                    <Star size={16} strokeWidth={2.5} className="text-[var(--duo-orange)]" fill="var(--duo-orange)" />
                    <span className="font-bold">{course.rating_avg ?? '—'}</span>
                    <span className="text-[var(--duo-text-soft)]">({course.rating_count} {t('reviews')})</span>
                  </div>
                )}
                <div className="flex items-center gap-1 text-[var(--duo-text-soft)]">
                  <Users size={16} strokeWidth={2.5} />
                  <span className="font-semibold">{getStudentCount(course)} {t('students')}</span>
                </div>
                <div className="flex items-center gap-1 text-[var(--duo-text-soft)]">
                  <BookOpen size={16} strokeWidth={2.5} />
                  <span className="font-semibold">{course.lessons_count || lessons.length} {t('lessons')}</span>
                </div>
              </div>
              <p className="text-sm text-[var(--duo-text-soft)] mt-3 font-medium">
                {t('instructor')}: <span className="font-bold text-[var(--duo-text)]">{course.instructor_name}</span>
              </p>
            </div>
            <div className="md:col-span-1">
              <motion.div
                className="card-duo p-6 sticky top-4"
                variants={maybe(reduced, bounceIn)}
                initial="hidden"
                whileInView="visible"
                viewport={VIEWPORT_ONCE}
              >
                <CourseCover
                  thumbnail={course.thumbnail}
                  category={course.category}
                  alt={courseDisplayTitle}
                  className="w-full rounded-xl mb-4 aspect-video"
                />
                {enrollment ? (
                  <>
                    <div className="mb-4">
                      <div className="flex justify-between text-sm font-semibold mb-2">
                        <span>{t('completion')}</span>
                        <span>{enrollment.progress}%</span>
                      </div>
                      <Progress
                        value={enrollment.progress}
                        className="h-2"
                        aria-label={t('courseDetail.progress.ariaLabel').replace('{{pct}}', enrollment.progress)}
                      />
                    </div>
                    <motion.button
                      onClick={handleContinue}
                      className="w-full btn-duo-primary py-3 text-lg"
                      variants={maybe(reduced, buttonSquash)}
                      initial="rest"
                      whileHover="hover"
                      whileTap="tap"
                      data-testid="continue-learning-btn"
                    >
                      {enrollment?.last_lesson_id && lessons.some(l => l.lesson_id === enrollment.last_lesson_id)
                        ? t('courseDetail.continueWhereLeft')
                        : t('continue_learning')}
                    </motion.button>
                    {enrollment.progress >= 100 && course?.final_exam?.enabled && (
                      <div className="mt-4 bg-[var(--surface-warm)] border-2 border-[var(--brand-accent-light)] rounded-xl p-4" data-testid="exam-prompt">
                        <div className="flex items-center gap-3">
                          <FileCheck size={28} className="text-[var(--duo-orange)] flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-bold text-sm text-[var(--duo-orange)]">
                              {t('courseDetail.finalExam.title')}
                            </div>
                            <div className="text-xs text-[var(--duo-text-soft)]">
                              {t('courseDetail.finalExam.sub')}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => navigate(`/exam/${courseId}`)}
                          className="mt-3 w-full btn-duo-blue px-4 py-2 text-sm"
                          data-testid="go-to-exam-btn"
                        >
                          {t('courseDetail.finalExam.cta')}
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="card-duo p-4 text-center bg-[var(--surface-soft)]" data-testid="enrollment-contact-card">
                    <MessageCircle size={32} strokeWidth={2} className="mx-auto mb-2 text-[var(--brand-primary)]" aria-hidden="true" />
                    <p className="font-duo-display text-base mb-1 text-[var(--text-primary)]">
                      {t('courseDetail.contactToEnroll.title')}
                    </p>
                    <p className="text-sm text-[var(--text-secondary)] mb-3">
                      {t('courseDetail.contactToEnroll.sub')}
                    </p>
                    {user ? (
                      <WhatsAppContactButton
                        variant="primary"
                        courseTitle={courseDisplayTitle}
                        className="w-full justify-center !text-[0.85rem]"
                        testId="contact-enrollment-btn"
                      />
                    ) : (
                      <div className="flex flex-col gap-2.5">
                        <Link
                          to="/login"
                          className="btn-duo-primary w-full justify-center"
                          data-testid="contact-enrollment-login-btn"
                        >
                          {t('courseDetail.contactToEnroll.ctaPrimaryGuest')}
                        </Link>
                        <WhatsAppContactButton
                          variant="secondary"
                          courseTitle={courseDisplayTitle}
                          className="w-full justify-center !text-[0.85rem]"
                          testId="contact-enrollment-whatsapp-btn"
                        />
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Tabs */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="curriculum" className="w-full">
          <TabsList className="bg-white border-2 border-[var(--duo-stroke)] rounded-xl p-1 mb-6">
            <TabsTrigger value="curriculum" className="rounded-lg font-bold" data-testid="tab-curriculum">
              {t('curriculum')}
            </TabsTrigger>
            <TabsTrigger value="reviews" className="rounded-lg font-bold" data-testid="tab-reviews">
              {t('reviews')} ({reviews.length})
            </TabsTrigger>
            <TabsTrigger value="about" className="rounded-lg font-bold" data-testid="tab-about">
              {t('about')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="curriculum">
            <CurriculumTab
              lessons={lessons}
              enrollment={enrollment}
              course={course}
              lang={lang}
              t={t}
              getField={getField}
              reduced={reduced}
              handleStartLesson={handleStartLesson}
              navigate={navigate}
              courseId={courseId}
            />
          </TabsContent>

          <TabsContent value="reviews">
            <div className="space-y-4">
              {/* Review Form */}
              {enrollment && (
                <ReviewForm
                  courseId={courseId}
                  authHeaders={authHeaders}
                  t={t}
                  lang={lang}
                  onSubmit={async () => {
                    const res = await axios.get(`${API}/courses/${courseId}`);
                    setReviews(res.data.reviews);
                    setCourse(res.data.course);
                  }}
                />
              )}
              {reviews.map((review, _i) => (
                <div key={review.review_id} className="card-duo p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-full bg-[var(--duo-blue)] flex items-center justify-center">
                      <span className="text-white text-sm font-bold">{review.user_name?.[0] || 'U'}</span>
                    </div>
                    <div>
                      <p className="font-bold text-sm">{review.user_name}</p>
                      <div className="flex gap-0.5">
                        {[...Array(5)].map((_, j) => (
                          <Star key={j} size={12} strokeWidth={2.5}
                            className={j < review.rating ? 'text-[var(--duo-orange)]' : 'text-gray-200'}
                            fill={j < review.rating ? 'var(--duo-orange)' : 'none'}
                          />
                        ))}
                      </div>
                    </div>
                    <span className="text-xs text-[var(--duo-text-soft)] ml-auto">
                      {new Date(review.created_at).toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-US')}
                    </span>
                  </div>
                  <p className="text-sm text-[var(--duo-text-soft)] font-medium">{review.comment}</p>
                </div>
              ))}
              {reviews.length === 0 && !enrollment && (
                <div className="text-center py-12 text-[var(--duo-text-soft)]">
                  <p className="font-semibold">{t('courseDetail.reviews.empty')}</p>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="about">
            <div className="card-duo p-6">
              <h3 className="font-duo-display text-xl font-bold mb-4">{courseDisplayTitle}</h3>
              <p className="text-[var(--duo-text-soft)] font-medium leading-relaxed">
                {getField(course, 'description')}
              </p>
              <div className="mt-6 grid grid-cols-2 gap-4">
                <div className="p-4 bg-[var(--surface-soft)] rounded-xl">
                  <p className="text-sm text-[var(--duo-text-soft)] font-semibold">{t('level')}</p>
                  <p className="font-bold">{t(course.level)}</p>
                </div>
                <div className="p-4 bg-[var(--surface-soft)] rounded-xl">
                  <p className="text-sm text-[var(--duo-text-soft)] font-semibold">{t('lessons')}</p>
                  <p className="font-bold">{course.lessons_count || lessons.length}</p>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </section>
    </div>
  );
}
